import { useMemo } from "react"

import styled from "styled-components"

import { marginTopBottomMixin, Margins } from "../../mixins"

import { BlurEventType, ClickEventType, UseFocusHandlingType } from "./types"

export const InvisibleSelector = styled.input.attrs<{
  type: "checkbox" | "radio"
}>(({ type }) => ({
  type,
}))`
  position: absolute;
  height: 1px;
  min-height: 1px;
  width: 0px;
  max-height: 1px;
  max-width: 0px;
  margin: 0px;
  padding: 0px;
  opacity: 0 !important;
  appearance: none;
`

const getCursor = ({ disabled, loading }: { disabled?: boolean; loading?: boolean }) => {
  if (loading) {
    return "wait"
  } else if (disabled) {
    return "not-allowed"
  } else {
    return "pointer"
  }
}

export const ControlContainer = styled.label<
  Margins & { disabled?: boolean; loading?: boolean }
>`
  line-height: 0;
  ${marginTopBottomMixin({ top: "none", bottom: "small" })}
  z-index: 1;
  position: relative;
  display: inline-flex;
  align-items: flex-start;
  cursor: ${getCursor};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
`

export const DecorativeContainer = styled.div`
  line-height: 0;
  display: inline-block;
`

export enum SelectorStateClassnames {
  HOVER = "selector--hover",
  ACTIVE = "selector--active",
}

export const selectorStates = {
  checked: `${InvisibleSelector}:checked ~ &`,
  indeterminate: `${InvisibleSelector}:indeterminate ~ &`,
  invalid: `${InvisibleSelector}[aria-invalid="true"] ~ &`,
  invalidChecked: `${InvisibleSelector}[aria-invalid="true"]:checked ~ &`,
  invalidIndeterminate: `${InvisibleSelector}[aria-invalid="true"]:indeterminate ~ &`,
  disabled: `${InvisibleSelector}:disabled ~ &`,
  disabledChecked: `${InvisibleSelector}:disabled:checked ~ &`,
  disabledIndeterminate: `${InvisibleSelector}:disabled:indeterminate ~ &`,
  focus: `${InvisibleSelector}:not(.no-focus):focus ~ &`,
  hover: `${ControlContainer}:hover &, .${SelectorStateClassnames.HOVER}:hover &`,
  active: `${ControlContainer}:active &, .${SelectorStateClassnames.ACTIVE}:active &`,
}

/**
 * Avoid focus styles on an element by setting a no-focus class onClick,
 * which is reset when the element is blurred.
 * */
export const useFocusHandling: UseFocusHandlingType = (ref, setNoFocus, onBlur) =>
  useMemo(() => {
    const handleClick: ClickEventType = (e) => {
      e.stopPropagation()

      if (ref.current) {
        ref.current.focus()
      }

      const target = e.target as HTMLElement
      if (target.tagName !== "INPUT") {
        setNoFocus(true)
      }
    }

    const handleBlur: BlurEventType = (e) => {
      if (onBlur) {
        onBlur(e)
      }
      setNoFocus(false)
    }

    return { handleClick, handleBlur }
  }, [onBlur, ref, setNoFocus])
